import { Component, ElementRef, HostBinding, OnDestroy, OnInit } from '@angular/core';
import { environment } from '../../environments/environment';
import { IPService } from '../api/ip.service';
import { Observable } from 'rxjs';
import { Select, Store } from '@ngxs/store';
import { Donation, DonationType } from '../transaction-store/donation';
import { OptIns } from '../transaction-store/opt-ins';
import { AppSettings } from '../app.settings';
import { Router } from '@angular/router';
import { TransactionActions } from '../transaction-store/transaction';
import { Frequency, Recurring } from '../transaction-store/recurring';
import { Sdk } from '../core/sdk.service';
import {
  buildApplePayRecurringPaymentRequest,
  buildDonorManagementUrl
} from '../shared/apple-pay-recurring';
import {
  Address,
  ApplePayPaymentRequest,
  ApplePaySessionStatus,
  ClientError,
  Contact,
  CreateDonationResult,
  PaymentGateway,
  PaymentMethod,
  PaymentMethodType,
  recaptcha,
  RecaptchaType,
  util,
  tokenize,
} from '@idonatedev/idonate-sdk';
import { PaymentInfo, PaymentInfoActions } from '../transaction-store/payment-info';
import {
  BillingInfo,
  BillingInfoActions,
} from '../transaction-store/billing-info';
import { CorporateMatching } from '../transaction-store/corporate-matching';
import { DesignationStateModel } from '../transaction-store/designation';
import { TributeInfo } from '../transaction-store/tribute-info';
import { Gift } from '../transaction-store/gift';
import * as addMonths from 'date-fns/add_months';
import * as endOfYesterday from 'date-fns/end_of_yesterday';
import * as isBefore from 'date-fns/is_before';
import { submitSiftTracking } from '../../init/sift';

interface PayPalCheckoutWindow extends Window {
  paypalCheckoutToken?: tokenize.PaymentToken;
}

@Component({
  selector: 'app-give-button',
  template: `
    <div class="h-100 container">
      <div class="h-100 row">
        <div class="col d-flex flex-column text-center">
          <button
            *ngIf="!isPayPalCheckoutActive()"
            type="submit"
            class="my-auto btn btn-primary"
            [disabled]="showLoading || captchaActive"
            (click)="resolveSubmission()"
            [attr.aria-label]="
              'give ' + (resolvedState.donation.amount + feeAmount)
            "
          >
            <span *ngIf="giveStatusText; else amountText">
              {{ giveStatusText }}
            </span>
            <ng-template #amountText>
              Give
              {{
                resolvedState.donation.amount + feeAmount
                  | currency: currencyCode:'symbol-narrow'
              }}
              Now
            </ng-template>
            <div
              *ngIf="showLoading || captchaActive"
              class="idonate-spinner"
              title="Loading..."
            ></div>
          </button>
          <div *ngIf="isPayPalCheckoutActive()" class="my-auto">
            <paypal-checkout
              [isFormValid]="isPayPalFormValid"
              (tokenReady)="onPayPalTokenReady($event)"
              (tokenError)="onPayPalError($event)"
              (validationFailed)="showValidation()"
            ></paypal-checkout>
          </div>
          <span class="secure-gateway float-right"></span>
        </div>
      </div>
    </div>
  `,
})
export class GiveButtonComponent implements OnInit, OnDestroy {
  @HostBinding('class.col') col = true;
  @Select((state) => state) state: Observable<any>;

  showLoading = false;
  captchaActive = false;
  giveStatusText = null;

  currencyCode = 'USD';
  valid = false;
  requireCustomDesignation = false;
  requireNotes = [];
  recaptchaType: RecaptchaType = 'invisible';
  recaptchaToken = null;

  feeAmount = 0;
  isPayPalFormValid = false;

  resolvedState: {
    donation: Donation;
    recurring: Recurring;
    corporateMatching: CorporateMatching;
    billingInfo: BillingInfo;
    paymentInfo: PaymentInfo;
    optIns: OptIns;
    designations: DesignationStateModel;
    tributeInfo: TributeInfo;
    gift: Gift;
  };

  dtdPublicKey = '';
  dtdPartnerKey = '';
  campaignId = '';
  sendAllDonationsToDtd = false;

  private captcha: recaptcha.RecaptchaElement;
  private repositionRecaptchaInterval;

  constructor(
    private ipService: IPService,
    private settings: AppSettings,
    private router: Router,
    private store: Store,
    private sdk: Sdk,
    private el: ElementRef
  ) {
    // keep a synchronized internal copy of global state to support existing code
    this.state.subscribe((newState) => {
      this.resolvedState = newState;

      // update internal values (e.g. feeAmount) based on external state
      if (newState.optIns.payFee) {
        this.feeAmount = parseFloat(newState.optIns.feeAmount.toFixed(2));
      } else {
        this.feeAmount = 0;
      }

      // The PayPal button container shows a disabled state when the form
      // isn't valid; recompute on every state change so it stays in sync.
      this.isPayPalFormValid = this.validate(newState);
    });

    const organization = this.settings.getValue('organization');
    this.currencyCode = organization.currency_code;
    this.dtdPublicKey = organization.details['doublethedonation.public_key'];
    this.dtdPartnerKey = organization.details['doublethedonation.partner_key'];
    this.sendAllDonationsToDtd = organization.features
      ? organization.features.includes('send_all_donations_dtd')
      : false;

    const properties = this.settings.getValue('properties');

    this.campaignId =
      properties.campaign_id ||
      (properties.campaign ? properties.campaign.id : undefined);

    this.requireCustomDesignation = Boolean(
      Number(properties.require_custom_designation)
    );

    this.requireNotes = [1, 2, 3, 4, 5]
      .map((i) => {
        return {
          id: i,
          required:
            this.settings.getValue('properties')[
              'custom_note_required_' + i.toString()
            ] || false,
          label:
            this.settings.getValue('properties')[
              'custom_note_label_' + i.toString()
            ],
        };
      })
      .filter((a) => !!a.label); // filter matching initial creation so we don't require note fields that aren't present

    // Only initialize reCaptcha if fraud checks are not bypassed
    const bypassFraudChecks = this.settings.getValue('bypassFraudChecks') || false;
    if (!bypassFraudChecks) {
      this.captcha = recaptcha.wrapElement(
        // captcha-challenge is defined in the parent component, which is awkward...
        // ...but not as awkward as the entire donation flow being inside give-button
        'captcha-challenge',
        environment.recaptchaInvisibleKey,
        {
          size: 'invisible',
        }
      );
    }
  }

  ngOnInit(): void {
    const repositionRecaptcha = () => {
      const iframes = document.querySelectorAll('iframe');
      let recaptchaIframe: HTMLIFrameElement | undefined;
      iframes.forEach(iframe => {
        if (
          iframe.src.startsWith('https://www.google.com/recaptcha/api2/bframe')
        ) {
          recaptchaIframe = iframe;
        }
      });

      if (recaptchaIframe) {
        const iframeContainer = recaptchaIframe.parentElement;
        const recaptchaModalContainer = iframeContainer.parentElement;

        if (iframeContainer && recaptchaModalContainer) {
          const submitButtonTopPos =
            this.el.nativeElement.getBoundingClientRect().top +
            document.documentElement.scrollTop;
          const modalHeight = parseInt(iframeContainer.style.height, 10);
          iframeContainer.style.top = `${Math.max(
            submitButtonTopPos - modalHeight + 50,
            0
          )}px`;
          iframeContainer.style.position = 'absolute';
          recaptchaModalContainer.style.width = '100vw';
          // Compiler hack, error TS2339: Property 'inset' does not exist on type 'CSSStyleDeclaration'
          (recaptchaModalContainer.style as any).inset = 'unset';
          recaptchaModalContainer.style.top = '0px';
          recaptchaModalContainer.style.bottom = '0px';
          recaptchaModalContainer.style.left = '0px';
          recaptchaIframe.style.border = '1px solid dadce8';
        }
      }
    };

    this.repositionRecaptchaInterval = setInterval(repositionRecaptcha, 200);
  }

  ngOnDestroy(): void {
    clearInterval(this.repositionRecaptchaInterval);
  }

  showValidation() {
    this.store.dispatch(new TransactionActions.ShowValidation());
  }

  isPayPalCheckoutActive(): boolean {
    if (
      !this.resolvedState ||
      !this.resolvedState.donation ||
      this.resolvedState.donation.type !== DonationType.PayPal
    ) {
      return false;
    }
    const paypalGateway: PaymentGateway = this.settings.getValue('paypalGateway');
    return paypalGateway && paypalGateway.backendName === 'paypal_checkout';
  }

  onPayPalTokenReady(token: tokenize.PaymentToken): void {
    (window as PayPalCheckoutWindow).paypalCheckoutToken = token;
    this.store.dispatch(new PaymentInfoActions.SetToken(token.paymentMethodId));
    // PayPal popup approval IS the donor's confirmation, so submit immediately.
    this.resolveSubmission();
  }

  onPayPalError(error: Error): void {
    console.error('[GiveButton] PayPal error:', error);
  }

  async resolveSubmission(): Promise<any> {
    /**
     * Donation processing starts here and proceeds as follows:
     **
     * Unauthenticated Donor, or Donor with "Add New" Payment Method:
     * - If Apple Pay tab is active
     *    - validate all fields except billing fields
     *    - Intiate Apple Pay and populate billing fields
     * - explicitly validate all input fields and bail out if invalid
     * - gather token data from payment-info
     * - enter a captcha-guarded context before calling create* methods:
     *   - pass gathered token data (payment and captcha) into createPaymentMethod, receive an ID
     *   - pass captcha token and payment ID into createDonation, receive transaction details
     * - on success, transaction details are passed to 'Thank You' feature.
     **
     * Authenticated Donor using a Saved Payment Method:
     * - explicitly validate all fields, bail out if invalid
     * - enter captcha-guarded context
     *   - pass captcha token and selected payment method ID into createTransaction
     * - success/fail handling is identical to unauthenticated
     */

    const { donation } = this.resolvedState;

    if (donation.type === DonationType.ApplePay) {
      // pre-validate the fields except the billing info, billing fields are not
      // available until Apple Pay approves the payment method
      const ignoredValidators = ['billing'];
      if (!this.validate(this.resolvedState, ignoredValidators)) {
        this.showValidation();
        return;
      }
      const organization = this.settings.getValue('organization');
      const applePayConfig: Partial<ApplePayPaymentRequest> = {
        currencyCode: organization.currency_code,
        countryCode: this.resolvedState.billingInfo.country,
        total: {
          label: organization.name,
          amount: (donation.amount + this.feeAmount).toString(),
        },
      };
      const recurringState = this.resolvedState.recurring;
      if (recurringState.frequency !== Frequency.Once) {
        // A recurring gift needs a merchant token (MPAN); this request is what
        // makes Apple issue one instead of a single-use device token.
        const cashFrequencies =
          this.settings.getValue('properties').cash_frequencies || {};
        const recurringPaymentRequest = buildApplePayRecurringPaymentRequest({
          frequency: recurringState.frequency,
          frequencyLabel:
            cashFrequencies[recurringState.frequency] ||
            recurringState.frequency,
          amount: applePayConfig.total.amount,
          organizationLabel: organization.name,
          managementURL: buildDonorManagementUrl(
            this.settings.getValue('donor_portal_url'),
            organization.slug,
            (organization.features || []).indexOf('branded_dss') >= 0
          ),
          endDate: recurringState.endDate
        });
        if (!recurringPaymentRequest) {
          this.showErrorMessage(
            'Recurring donations with Apple Pay are not available for this frequency.'
          );
          return;
        }
        applePayConfig.recurringPaymentRequest = recurringPaymentRequest;
      }
      // wait for apple pay to initiate and populate required data
      // and set billing contact based on Apple's data
      await this.sdk.initApplePay(applePayConfig);
      this.store.dispatch(
        new BillingInfoActions.UpdateBillingInfo(
          this.sdk.applePayResponse.billingContact
        )
      );
    }

    if (!this.validate(this.resolvedState)) {
      const billing = this.resolvedState.billingInfo;
      const isWallet = donation.type === DonationType.GooglePay || donation.type === DonationType.ApplePay;
      console.warn('[GiveButton] validation failed:', {
        recurring: this.checkRecurringInfo(this.resolvedState.recurring),
        billing: this.checkBillingInfo(billing, isWallet),
        billingData: { firstName: billing.firstName, lastName: billing.lastName, email: billing.email, country: billing.country },
        designations: this.checkDesignation(this.resolvedState.designations),
        payments: this.checkPaymentInfo(this.resolvedState.donation, this.resolvedState.paymentInfo),
        notes: this.checkCustomNotes(this.resolvedState.designations.notes),
      });
      this.showValidation();
      return;
    }

    const {
      billingInfo,
      paymentInfo,
      corporateMatching,
      designations,
      recurring,
      tributeInfo,
      gift,
      optIns,
    } = this.resolvedState;


    // submit to Sift now that we've got a userID & have passed form validation
    submitSiftTracking(billingInfo.email);

    const { thanks_redirect_url, customer_meta } =
      this.settings.getValue('properties');

    let parsedCustomerMeta = {};

    if (customer_meta) {
      // convert JSON string into Object, if required
      if (typeof customer_meta === 'object') {
        parsedCustomerMeta = customer_meta;
      } else {
        try {
          parsedCustomerMeta = JSON.parse(customer_meta);
        } catch {}
      }
    }

    const validAddress: Address = {
      address1: billingInfo.address1,
      address2: billingInfo.address2,
      city: billingInfo.city,
      state: billingInfo.state,
      zip: billingInfo.zip,
      country: billingInfo.country,
    };

    const validContact: Contact = {
      firstName: billingInfo.firstName,
      middleName: billingInfo.middleName,
      lastName: billingInfo.lastName,
      email: billingInfo.email,
      salutation: billingInfo.title,
      primaryPhone: billingInfo.phone,
      company: billingInfo.company,
    };

    let paymentGateway: PaymentGateway;

    if (donation.type === DonationType.PayPal) {
      paymentGateway = this.settings.getValue('paypalGateway');
    } else if (
      donation.type === DonationType.ApplePay ||
      donation.type === DonationType.GooglePay
    ) {
      paymentGateway = this.settings.getValue('digitalWalletGateway');
    } else {
      paymentGateway = this.settings.getValue('primaryGateway');
    }

    if (!paymentGateway || !paymentGateway.backendName || !paymentGateway.id) {
      console.error('[GiveButton] invalid gateway:', paymentGateway);
      throw new Error(`Invalid Payment Gateway for ${donation.type}`);
    }

    try {
      this.clearErrorMessage();

      this.showLoading = true;

      const bypassFraudChecks = this.settings.getValue('bypassFraudChecks') || false;
      const isPayPalCheckout =
        donation.type === DonationType.PayPal &&
        paymentGateway.backendName === 'paypal_checkout';

      if (isPayPalCheckout) {
        // The PayPal order_id was registered server-side as a captcha
        // nonce by /paypal/create-order; gmsnext's recaptcha_service treats
        // captchaType='paypal_checkout' as server-validated.
        const paypalToken = (window as PayPalCheckoutWindow).paypalCheckoutToken;
        if (!paypalToken || !paypalToken.token) {
          throw new Error('PayPal Checkout token not available');
        }
        this.recaptchaToken = paypalToken.token;
        this.recaptchaType = 'paypal_checkout' as RecaptchaType;
        this.giveStatusText = 'Submitting...';
      } else if (bypassFraudChecks) {
        // When fraud checks are bypassed, use 'bypass' for both type and token
        this.recaptchaToken = 'bypass';
        this.recaptchaType = 'bypass' as RecaptchaType;
        this.giveStatusText = 'Submitting...';
      } else {
        this.captchaActive = true;
        this.giveStatusText = 'Verifying...';

        if (donation.type === DonationType.ApplePay) {
          this.recaptchaToken = this.sdk.applePayResponse.merchantSessionId;
          this.recaptchaType = 'nonce';
        } else {
          this.recaptchaToken = await this.captcha.resolveToken();
          this.recaptchaType = 'invisible';
        }

        this.captchaActive = false;
        this.giveStatusText = 'Submitting...';
      }

      const paymentMethod = await this.resolvePaymentMethod(
        paymentGateway,
        validContact,
        validAddress,
        this.recaptchaToken,
        paymentInfo,
        donation.type
      );

      this.giveStatusText = 'Processing...';

      // not too happy with the data-resolving split between this method
      // (resolveSubmission) and resolveDonation here -- data still coming in
      // from too many directions
      const donationResult = await this.resolveDonation(
        paymentGateway,
        paymentMethod,
        validContact,
        validAddress,
        donation,
        recurring,
        corporateMatching,
        tributeInfo,
        gift,
        // isAnonymous
        designations,
        parsedCustomerMeta
      );

      this.giveStatusText = 'Complete!';
      this.showLoading = false;

      // Clear the token so it can't be reused on the next donation
      this.store.dispatch(new PaymentInfoActions.ClearToken());
      (window as PayPalCheckoutWindow).paypalCheckoutToken = undefined;

      // store the raw result in the state -- this is used for the Thank You screen
      // TODO some other time : rewrite Thank You to use structured data
      this.store.dispatch(
        new TransactionActions.SetTransaction(donationResult._raw_response)
      );

      const { doublethedonation } = window as any;
      if (
        (this.sendAllDonationsToDtd || corporateMatching.selected) &&
        doublethedonation
      ) {
        try {
          doublethedonation.integrations.core.register_donation({
            '360matchpro_public_key': this.dtdPublicKey,
            partner_identifier: this.dtdPartnerKey,
            campaign: this.campaignId,
            donation_identifier: donationResult.transaction.id,
            donation_amount: donation.amount,
            donor_first_name: billingInfo.firstName,
            donor_last_name: billingInfo.lastName,
            donor_email: billingInfo.email,
            donor_address: {
              zip: billingInfo.zip,
              city: billingInfo.city,
              state: billingInfo.state,
              address1: billingInfo.address1,
              address2: billingInfo.address2,
              country: billingInfo.country,
            },
            donor_phone: billingInfo.phone,
            donation_datetime: new Date().toISOString(),
            anonymous: optIns.anonymous,
            in_memoriam: tributeInfo.completed.toString(),
            recurring: (recurring.frequency !== Frequency.Once).toString(),
            doublethedonation_company_id: corporateMatching.selected,
          });
        } catch (err) {
          console.error(err);
        }
      }

      // retain existing behavior of notifying our parent that the donation is complete,
      // but don't wait around for a response anymore - just navigate!
      if (window.parent) {
        // hosted pages bump their goal meter off these
        const gift =
          donationResult._raw_response.transaction ||
          donationResult._raw_response.schedule ||
          {};
        window.parent.postMessage(
          JSON.stringify({
            embed_id: this.settings.getValue('id'),
            action: 'transactionComplete',
            transaction_id: gift.id,
            amount: gift.total_value || gift.amount,
          }),
          '*'
        );

        window.parent.postMessage(
          JSON.stringify({
            embed_id: this.settings.getValue('id'),
            scrollIntoView: true,
          }),
          '*'
        );
      }

      // Show success and close the Apple Payment sheet
      if (donation.type === DonationType.ApplePay) {
        this.sdk.completeApplePayPayment(ApplePaySessionStatus.STATUS_SUCCESS);
      }

      if (thanks_redirect_url) {
        const redirectUrl = buildRedirectUrl(
          thanks_redirect_url,
          donationResult._raw_response
        );

        // Set up fallback: if no redirect ack within 2 seconds, show thank-you page
        let redirectTimeout: ReturnType<typeof setTimeout> | null = null;
        let redirectAckReceived = false;

        const redirectAckHandler = (evt: MessageEvent) => {
          try {
            const evtData = JSON.parse(evt.data);
            if (evtData && evtData.embedAction === 'redirectAck') {
              redirectAckReceived = true;
              window.removeEventListener('message', redirectAckHandler);
              if (redirectTimeout) {
                clearTimeout(redirectTimeout);
              }
              // idonatelib will handle the redirect - nothing more to do
            }
          } catch {
            // Ignore non-JSON messages
          }
        };

        window.addEventListener('message', redirectAckHandler);

        // Send redirect request to parent via postMessage (fixes Firefox popup blocker)
        window.parent.postMessage(
          JSON.stringify({
            embed_id: this.settings.getValue('id'),
            action: 'redirect',
            url: redirectUrl,
          }),
          '*'
        );

        // Fallback: if no ack within 2 seconds, show thank-you page
        // This handles cases where idonatelib isn't present or redirect is blocked
        redirectTimeout = setTimeout(() => {
          if (!redirectAckReceived) {
            window.removeEventListener('message', redirectAckHandler);
            // Redirect failed - show success page instead so user sees confirmation
            this.router.navigate(['thank-you']);
          }
        }, 2000);
      } else {
        // TODO investigate: previously, `navigate` would be called even when a redirect is configured,
        //                   leaving too much ambiguity as to what is expected to happen - investigate
        //                   if removing this blind navigation introduces any unwanted side effects.
        return this.router.navigate(['thank-you']);
      }
    } catch (err) {
      // ANY errors from this process should be displayed
      console.error('resolveSubmission', err);

      this.showLoading = false;
      this.giveStatusText = null;

      if (donation.type === DonationType.ApplePay) {
        this.sdk.completeApplePayPayment(ApplePaySessionStatus.STATUS_FAILURE);
      }

      if (err instanceof ClientError) {
        if (err.message && err.message.length) {
          this.showErrorMessage(err.message);
        } else {
          // show SOMETHING, even if the error is blank
          this.showErrorMessage('ERROR');
        }
      } else {
        this.showErrorMessage(err.message || err.toString());

        // unhandled.. throw it up in case something else wants it.
        throw err;
      }
    }
  }

  async resolvePaymentMethod(
    paymentGateway: PaymentGateway,
    contact: Contact,
    address: Address,
    recaptchaToken: string,
    paymentInfo: PaymentInfo,
    donationType: DonationType
  ): Promise<PaymentMethod> {
    let paymentMethodType: PaymentMethodType;

    switch (donationType) {
      case DonationType.Credit:
      case DonationType.Debit:
        paymentMethodType = 'credit_card';
        break;
      case DonationType.Check:
        paymentMethodType = 'bank_account';
        break;
      case DonationType.PayPal:
        paymentMethodType = 'paypal';
        break;
      case DonationType.ApplePay:
        paymentMethodType = 'apple_pay';
        break;
      case DonationType.GooglePay:
        paymentMethodType = 'google_pay';
        break;
      default:
        throw new Error(`Unhandled payment type: ${donationType}`);
    }

    if (paymentInfo.method && paymentInfo.method !== 'new') {
      // return stored payment method directly
      // TODO : rely on tokenStrategy === 'payment_method' instead?
      return {
        id: paymentInfo.method,
        type: paymentMethodType,
      };
    }

    // PayPal Checkout: the SDK already created a payment_method server-side
    // when the order was created; we just forward its ID.
    if (paymentGateway.backendName === 'paypal_checkout') {
      const paypalToken = (window as PayPalCheckoutWindow).paypalCheckoutToken;
      if (!paypalToken || !paypalToken.paymentMethodId) {
        throw new Error('PayPal Checkout token not available');
      }
      return {
        id: paypalToken.paymentMethodId,
        type: paymentMethodType,
      };
    }

    let paymentMethodToken: string;

    if (paymentGateway.backendName === 'spreedly') {
      paymentMethodToken = await this.resolvePaymentMethodTokenSpreedly(
        paymentMethodType,
        paymentInfo,
        contact,
        address
      );
    } else if (paymentGateway.backendName === 'card_connect') {
      paymentMethodToken = await this.resolvePaymentMethodTokenCardConnect(
        paymentMethodType,
        paymentInfo
      );
    } else {
      throw new Error(`Unhandled backend: ${paymentGateway.backendName}`);
    }

    if (!paymentMethodToken) {
      throw new Error(
        `paymentMethodToken not resolved: ${paymentGateway.backendName}:${paymentMethodType}`
      );
    }

    return this.sdk.client
      .createPaymentMethod({
        contact,
        address,
        paymentGatewayId: paymentGateway.id,
        paymentMethodToken,
        paymentMethodType,
        recaptchaType: this.recaptchaType,
        recaptchaToken,
      })
      .then((result) => {
        return {
          id: result.paymentMethodId,
          type: paymentMethodType,
        };
      });
  }

  private async resolvePaymentMethodTokenSpreedly(
    paymentMethodType: PaymentMethodType,
    paymentInfo: PaymentInfo,
    contact: Contact,
    address: Address
  ): Promise<string> {
    if (paymentMethodType === 'credit_card') {
      // Get the active payment component from the SDK
      const activeComponent = this.sdk.activePaymentComponent;

      if (activeComponent && activeComponent.getPaymentType() === 'credit_card') {
        const tokenizationPayload = {
          firstName: contact.firstName,
          lastName: contact.lastName,
          email: contact.email,
          phone: contact.primaryPhone,
          expiryMonth: paymentInfo.month || '', // Empty string if not in state
          expiryYear: paymentInfo.year || '',   // Empty string if not in state
          address: {
            address1: address.address1,
            address2: address.address2,
            city: address.city,
            state: address.state,
            zip: address.zip,
            country: address.country,
          }
        };

        const result = await activeComponent.tokenize(tokenizationPayload);
        return result.token;
      }

      throw new Error('No tokenizer available for credit card');
    } else if (paymentMethodType === 'bank_account') {
      // Get the active payment component from the SDK
      const activeComponent = this.sdk.activePaymentComponent;

      if (activeComponent && activeComponent.getPaymentType() === 'bank_account') {
        const tokenizationPayload = {
          firstName: contact.firstName,
          lastName: contact.lastName,
          email: contact.email,
          phone: contact.primaryPhone,
          // For unified tokenizer, routing and account are provided by the tokenizer's internal fields
          // Only include them if they exist in state (legacy non-iframe flow)
          ...(paymentInfo.routing && { routingNumber: paymentInfo.routing }),
          ...(paymentInfo.account && { accountNumber: paymentInfo.account }),
          accountType: 'checking' as const,
          accountHolderType: 'personal' as const,
          address: {
            address1: address.address1,
            address2: address.address2,
            city: address.city,
            state: address.state,
            zip: address.zip,
            country: address.country,
          }
        };

        const result = await activeComponent.tokenize(tokenizationPayload);
        return result.token;
      }

      throw new Error(`Tokenization failed for bank account`);
    } else if (paymentMethodType === 'paypal') {
      // PayPal doesn't use iframe tokenization - it's handled via offsite redirect
      // Create a simple token with contact/address info for Spreedly
      return await tokenize.SpreedlyTokenizer.tokenizePayPal(
        { contact, address },
        this.sdk.client.config
      );
    }

    throw new Error(`Invalid Spreedly methodType: ${paymentMethodType}`);
  }

  private async resolvePaymentMethodTokenCardConnect(
    paymentMethodType: PaymentMethodType,
    paymentInfo: PaymentInfo
  ): Promise<string> {
    if (paymentMethodType === 'credit_card') {
      // Get the active payment component from the SDK
      const activeComponent = this.sdk.activePaymentComponent;

      if (activeComponent && activeComponent.getPaymentType() === 'credit_card') {
        const result = await activeComponent.tokenize({});
        return result.token;
      }

      throw new Error('No tokenizer available for CardConnect credit card');
    } else if (paymentMethodType === 'bank_account') {
      // Get the active payment component from the SDK
      const activeComponent = this.sdk.activePaymentComponent;

      if (activeComponent && activeComponent.getPaymentType() === 'bank_account') {
        const tokenizationPayload = {
          // For unified tokenizer, routing and account are provided by the tokenizer's internal fields
          // Only include them if they exist in state (legacy non-iframe flow)
          ...(paymentInfo.routing && { routingNumber: paymentInfo.routing }),
          ...(paymentInfo.account && { accountNumber: paymentInfo.account }),
          accountType: 'checking',
          accountHolderType: 'personal',
        };

        const result = await activeComponent.tokenize(tokenizationPayload);
        return result.token;
      }

      throw new Error(`Tokenization failed for bank account`);
    } else if (paymentMethodType === 'apple_pay') {
      return this.sdk.applePayResponse.cardConnectResponse.token;
    } else if (paymentMethodType === 'google_pay') {
      return this.sdk.googlePayResponse.cardConnectResponse.token;
    }
    throw new Error(`Invalid CardConnect methodType: ${paymentMethodType}`);
  }

  async resolveDonation(
    paymentGateway: PaymentGateway,
    paymentMethod: PaymentMethod,
    contact: Contact,
    address: Address,
    donation: Donation,
    recurring: Recurring,
    corporateMatching: CorporateMatching,
    tribute: TributeInfo,
    gift: Gift,
    designations: DesignationStateModel,
    customerMeta: { [key: string]: string }
  ): Promise<CreateDonationResult> {
    // would be nice for all of these values to come packaged together, with types..
    // would also be nice for property extraction to happen elsewhere in the flow
    const {
      campaign,
      advocate_id,
      campaign_id,
      organization_event_id,
      page_id,
      p2p_fundraiser_id,
      reference_code,
      advocacy_team_id,
      utm_medium,
      utm_term,
      utm_content,
      utm_source,
      utm_campaign,
    } = this.settings.getValue('properties');

    const resolvedCampaignId =
      campaign_id || (campaign ? campaign.id : undefined);

    let resolvedDesignations: { id: string; amount: number }[];

    if (designations.items) {
      resolvedDesignations = designations.items.map((d) => ({
        id: d.id,
        amount: d.amount,
      }));
    } else {
      resolvedDesignations = [];
    }

    const resolvedCustomerMeta = {};

    const selectNote = (item) =>
      item ? util.sanitizeString(item.value) : undefined;

    if (designations.notes) {
      [1, 2, 3, 4, 5].forEach((index) => {
        const note = selectNote(
          designations.notes.find((item) => item.id === index)
        );
        if (note) {
          resolvedCustomerMeta[`custom_note_${index}`] = note;
        }
      });
    }

    if (customerMeta) {
      Object.assign(resolvedCustomerMeta, customerMeta);
    }

    let resolvedTribute;

    if (tribute.id) {
      resolvedTribute = util.sanitizeObject({
        tributeDefinitionId: tribute.id,
        tributeFirstName: tribute.firstName,
        tributeLastName: tribute.lastName,
        tributeAddress1: tribute.address1,
        tributeAddress2: tribute.address2,
        tributeCity: tribute.city,
        tributeState: tribute.state,
        tributePostalCode: tribute.zip,
        tributeCountry: tribute.country || 'US',
        tributeFromName: tribute.from,
        tributeRecipientEmail: tribute.email,
        honorees: tribute.honorees,
        tributeSendCard: false,
        tributeIncludeAmount: false,
        useOnBillingAddress: false,
        tributeSkipped: !tribute.completed,
      });
    }

    return this.sdk.client
      .createDonation({
        // donorId: '', do we send this for logged in users?? TODO
        embedId: this.settings.getValue('id'),
        billingAddress: address,
        billingContact: contact,

        paymentGatewayId: paymentGateway.id,
        currency: this.currencyCode,

        paymentMethodId: paymentMethod.id,
        recurringFrequency: recurring.frequency,
        recurringStart: recurring.startDate,
        recurringEnd: recurring.endDate,
        campaignId: resolvedCampaignId,

        designations: resolvedDesignations,

        corporateMatchingId: corporateMatching.selected || undefined,

        // pass-through embed settings (or url params)
        landingPageId: page_id,
        p2pFundraiserId: p2p_fundraiser_id,
        p2pFundraiserComment: designations.p2pComments
          ? util.sanitizeString(designations.p2pComments)
          : undefined,
        designationNote: designations.custom
          ? util.sanitizeString(designations.custom)
          : undefined,
        organizationEventId: organization_event_id,
        advocateId: advocate_id,
        advocacyTeamId: advocacy_team_id,

        // paymentAmount is the total amount to charge -- why is feeAmount not part of donation?
        paymentAmount: donation.amount + this.feeAmount,

        // For paypal_checkout, the SDK created a payment_transaction record
        // server-side with the order_id; the donation must reference it so
        // the backend can capture the order during createDonation.
        paymentTransactionId:
          paymentGateway.backendName === 'paypal_checkout'
            ? (
                (window as PayPalCheckoutWindow).paypalCheckoutToken ||
                ({} as tokenize.PaymentToken)
              ).paymentTransactionId
            : undefined,

        recaptchaType: this.recaptchaType,
        recaptchaToken: this.recaptchaToken,
        referenceCode: reference_code || undefined,

        // TODO : figure out what other errata can be passed as meta then extracted in the SDK
        customerMeta: resolvedCustomerMeta,
        utm: {
          campaign: utm_campaign,
          content: utm_content,
          medium: utm_medium,
          source: utm_source,
          term: utm_term
        },

        emailOptIn: this.resolvedState.optIns.emails,
        anonymousOptIn: this.resolvedState.optIns.anonymous,
        showNameToFundraiserOptIn: this.resolvedState.optIns.showForFundraiser,
        donorPaidFeeAmount: this.feeAmount || undefined,

        tribute: resolvedTribute,

        giftId: gift.id,
        giftSkipped: !gift.completed,
        giftExtra: gift.id ? util.sanitizeString(gift.extra) : undefined
      })
      .then((result: CreateDonationResult) => {
        // handle offsite payments (currently only Spreedly: PayPal)
        if (result._raw_response.checkout_url) {
          // try to pop open a new window
          const offsiteWindow = window.open(
            result._raw_response.checkout_url,
            'Continue Payment',
            'width=600,height=800'
          );

          // show error if popup was blocked, prompting to unblock the popup
          if (!offsiteWindow) {
            this.showErrorMessage(
              'Payment Popup was blocked. Please allow the popup or disable your popup blocker and try again.'
            );
          }

          // poll the backend for offsite purchase results - even if the popup fails to open.
          // if the popup fails to open, the user is given a chance to allow it
          // in this case, it will immediately pop up - but the offsiteWindow reference will still be empty.
          return this.sdk.client.waitForDonationResult(result._raw_response.id);
        } else {
          return result;
        }
      });
  }

  showErrorMessage(message: string) {
    this.store.dispatch(new TransactionActions.ShowError({ error: message }));
  }

  clearErrorMessage() {
    this.store.dispatch(new TransactionActions.ShowError({ error: '' }));
  }

  validate(state, ignore?) {
    let valid = true;
    if (!ignore) {
      ignore = [];
    }

    if (!ignore.includes('recurring')) {
      valid = valid && this.checkRecurringInfo(state.recurring);
    }
    if (!ignore.includes('billing')) {
      const isWalletPayment =
        state.donation.type === DonationType.GooglePay ||
        state.donation.type === DonationType.ApplePay;
      valid = valid && this.checkBillingInfo(state.billingInfo, isWalletPayment);
    }
    if (!ignore.includes('designations')) {
      valid = valid && this.checkDesignation(state.designations);
    }
    if (!ignore.includes('payments')) {
      valid = valid && this.checkPaymentInfo(state.donation, state.paymentInfo);
    }
    if (!ignore.includes('notes')) {
      valid = valid && this.checkCustomNotes(state.designations.notes);
    }
    return valid;
  }

  checkCustomNotes(notes): boolean {
    let bValid = true;
    // i don't understand angular well enough to know why but the validate() method is
    // called multiple times, even prior to the constructor somehow so I have to guard
    // for it.

    if (this.requireNotes == null) {
      return true;
    }

    this.requireNotes
      .filter((a) => a.required)
      .forEach((element) => {
        if (bValid) {
          const note = notes.filter((a) => a.id === element.id);
          if (note[0] == null || note[0].value == null) {
            bValid = false;
            return;
          }
          // the method responsible for the note value defines it as label = value, and will
          // leave label = (nothing) if you happen to remove the elements after setting it.
          const pieces = note[0].value.split('=');
          // satisfy an edge case where someone ends their note with a =
          if (pieces.length > 2) {
            return;
          }
          const actualNote = pieces.slice(-1);
          if (actualNote[0] == null || actualNote[0].trim() === '') {
            bValid = false;
          }
        }
      });
    return bValid;
  }

  validateEmail(email) {
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  }

  checkBillingInfo(billing, isWalletPayment = false): boolean {
    const requireTitle =
      this.settings.getValue('properties').require_title || false;
    const requirePhone =
      this.settings.getValue('properties').require_phone || false;
    const baselineValid =
      billing.firstName &&
      billing.lastName &&
      billing.email &&
      this.validateEmail(billing.email) &&
      billing.country &&
      billing.address1 &&
      billing.city &&
      billing.state &&
      !!billing.zip;
    // Wallet payments (Google Pay, Apple Pay) don't provide title/salutation,
    // and billing fields are hidden so the user can't enter them manually
    const optionalValid = isWalletPayment ||
      (!!(billing.title || !requireTitle) && !!(billing.phone || !requirePhone));

    return baselineValid && optionalValid;
  }

  checkDesignation(designations): boolean {
    // Make sure we have designations available
    const availableDesignations = this.settings
      .getValue('organization')
      .designations.filter((item) => item.is_active);

    if (
      !designations.active ||
      this.settings.getValue('properties').designation_lock ||
      !availableDesignations.length
    ) {
      return true; // designations are not enabled, so pass through.
    }
    const customDesignationLabel =
      this.settings.getValue('properties').custom_designation_label;
    const viableCustomDesignation =
      !!customDesignationLabel && customDesignationLabel.trim() !== '';

    if (
      viableCustomDesignation &&
      this.requireCustomDesignation &&
      !designations.custom
    ) {
      // TODO : error feedback for custom designations
      return false;
    }

    // otherwise, at least one designation is required? (this is true in app4, at least.)
    return designations.items.length > 0;
  }

  checkRecurringInfo(recurring) {
    const isRecurringFrequency = recurring.frequency !== Frequency.Once;
    const recurringMethod =
      this.settings.getValue('properties').recurring_method;
    const count = recurring.count;
    const startDate = recurring.startDate;
    const endDate = recurring.endDate;
    const yesterday = endOfYesterday();
    const threeMonthsOut = addMonths(new Date(), 3);
    if (!isRecurringFrequency) {
      return true;
    }
    if (!startDate || startDate < yesterday || startDate > threeMonthsOut) {
      return false;
    }
    if (recurringMethod === 'count' && (!count || count <= 0)) {
      return false;
    }
    if (recurringMethod === 'date' && endDate && isBefore(endDate, startDate)) {
      return false;
    }
    return true;
  }

  checkPaymentInfo(donation, payment): boolean {
    if (
      donation.amount < this.settings.getValue('minimumAmount') ||
      donation.amount > this.settings.getValue('maximumAmount')
    ) {
      return false;
    }
    if (payment.method) {
      return true;
    } else if (donation.type === DonationType.Check) {
      // The tokenizer iframe reports per-field validation issues via
      // tokenValidationError (e.g. browser/Google autofill stuffing the CVV
      // with masked stars). Refuse to submit while that error is live.
      if (payment.tokenValidationError) {
        return false;
      }
      // Check if we have an active payment component that will handle validation
      const hasActiveComponent = !!this.sdk.activePaymentComponent;

      return (
        !!payment.token ||
        hasActiveComponent || // Trust the component to validate internally
        (!!payment.routing && !!payment.account) // Legacy validation fallback
      );
    } else if (donation.type === DonationType.PayPal) {
      return true;
    } else if (donation.type === DonationType.ApplePay) {
      return true;
    } else if (donation.type === DonationType.GooglePay) {
      if (!this.sdk.googlePayResponse.cardConnectResponse) {
        return false;
      }
      return !!this.sdk.googlePayResponse.cardConnectResponse.token;
    } else {
      if (payment.tokenValidationError) {
        return false;
      }
      // Check if we have an active payment component that will handle validation
      const hasActiveComponent = !!this.sdk.activePaymentComponent;

      return (
        !!payment.token ||
        hasActiveComponent || // Trust the component to validate internally
        (!!payment.month && !!payment.year) // Legacy validation fallback
      );
    }
  }
}

function buildRedirectUrl(baseUrl, donationResponse) {
  function serializeUrlParams(obj, prefix?) {
    const str = [];
    for (const p in obj) {
      if (obj.hasOwnProperty(p)) {
        const k = prefix ? prefix + '[' + p + ']' : p,
          v = obj[p];
        str.push(
          v !== null && typeof v === 'object'
            ? serializeUrlParams(v, k)
            : encodeURIComponent(k) + '=' + encodeURIComponent(v)
        );
      }
    }
    return str.join('&');
  }

  const {
    transaction,
    schedule,
    form_data: formData,
    designation,
  } = donationResponse;

  let trans_fields = {};
  let sched_fields = {};

  if (transaction) {
    trans_fields = {
      id: transaction.id,
      created: transaction.created,
      description: transaction.description,
      status: transaction.status,
      subtype: transaction.subtype,
      type: transaction.type,
      value: transaction.total_value,
    };
  }
  if (schedule) {
    sched_fields = {
      id: schedule.id,
      created: schedule.created,
      value: schedule.amount,
      frequency: schedule.schedule_type,
      // not sure why we trust formData here
      type: formData ? formData.type : undefined,
    };
  }

  // transaction_fields will take precendence and overwrite any schedule_fields
  const params: any = {
    ...sched_fields,
    ...trans_fields,
  };

  params.donor = {
    contact: {
      created: donationResponse.donor.contact.created,
      title: donationResponse.donor.contact.title,
      updated: donationResponse.donor.contact.updated,
    },
    id: donationResponse.donor.id,
    created: donationResponse.donor.created,
  };

  if (formData) {
    params.designation_note = formData.designation_note;
  }

  if (designation) {
    params.designation_id = designation.id;
    params.designation_title = designation.title;
  }

  // maintain existing behavior: delete undefined or null values
  Object.keys(params).forEach(
    (key) =>
      (params[key] === undefined || params[key] === null) && delete params[key]
  );

  return `${baseUrl}?${serializeUrlParams(params)}`;
}
